import { useMemo, useState } from "react";
import { toast } from "sonner";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { beatMax, shuffle } from "../beatUtils";
import { Briefing } from "./Shared";
import { Wrench, Check, X } from "lucide-react";

// Simple, distinct line-art instrument icons.
const ICONS = {
  scissors: (
    <svg viewBox="0 0 48 48" className="w-full h-12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <circle cx="12" cy="36" r="5" /><circle cx="24" cy="36" r="5" />
      <line x1="14.5" y1="32" x2="40" y2="8" /><line x1="21.5" y1="32" x2="40" y2="16" />
    </svg>
  ),
  haemostat: (
    <svg viewBox="0 0 48 48" className="w-full h-12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <circle cx="12" cy="38" r="4.5" /><circle cx="22" cy="38" r="4.5" />
      <line x1="14" y1="34.5" x2="30" y2="14" /><line x1="20" y1="34.5" x2="30" y2="18" />
      <path d="M30 14 L40 6 M30 18 L40 12" /><rect x="22" y="24" width="5" height="5" transform="rotate(40 24 26)" />
    </svg>
  ),
  needleHolder: (
    <svg viewBox="0 0 48 48" className="w-full h-12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <circle cx="12" cy="38" r="4.5" /><circle cx="22" cy="38" r="4.5" />
      <line x1="14" y1="34.5" x2="28" y2="16" /><line x1="20" y1="34.5" x2="28" y2="20" />
      <path d="M26 14 a7 7 0 1 1 8 6" />
    </svg>
  ),
  towelClamp: (
    <svg viewBox="0 0 48 48" className="w-full h-12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <circle cx="13" cy="38" r="4.5" /><circle cx="23" cy="38" r="4.5" />
      <path d="M15 34.5 C 22 24, 22 16, 28 10" /><path d="M21 34.5 C 26 24, 30 18, 36 12" />
      <path d="M28 10 C 31 14, 34 14, 36 12" />
    </svg>
  ),
  scalpel: (
    <svg viewBox="0 0 48 48" className="w-full h-12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="10" y="30" width="22" height="6" rx="2" transform="rotate(-35 21 33)" />
      <path d="M30 16 L40 8 L38 20 Z" fill="currentColor" stroke="none" />
    </svg>
  ),
  forceps: (
    <svg viewBox="0 0 48 48" className="w-full h-12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M18 8 L22 34 L24 38 L26 34 L30 8" />
    </svg>
  ),
};

export function InstrumentTrayBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const options = useMemo(() => shuffle(beat.options), [beat]);
  const [pick, setPick] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;
  const correct = beat.options.find((o) => o.correct);

  const onPrimary = () => {
    if (!checked) {
      if (pick == null) return toast.error("Select an instrument.");
      const ok = options[pick].correct;
      setRes({ earned: ok ? (beat.full ?? 20) : 0, max: beatMax(beat), detail: ok ? "Correct instrument" : "Wrong instrument", key: beat.key, label: beat.label });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Wrench}
      eyebrow={eyebrow}
      title={beat.title}
      subtitle={beat.prompt}
      primaryLabel={checked ? (beat.last ? "See debrief →" : "Continue →") : "Pass the instrument"}
      onPrimary={onPrimary}
      primaryTestId={`${beat.key}-primary-btn`}
      accent={accent}
    >
      {showBriefing && <Briefing c={c} />}

      <div className="rounded-lg border-2 border-slate-300 bg-gradient-to-b from-slate-100 to-slate-200 p-3" data-testid="instrument-tray">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Instrument tray</div>
        <div className="grid grid-cols-3 gap-2">
          {options.map((o, i) => {
            const selected = pick === i;
            let cls = "border-slate-300 bg-white text-slate-600 hover:border-[#0d9488]";
            if (checked) {
              if (o.correct) cls = "border-emerald-400 bg-emerald-50 text-emerald-700";
              else if (selected) cls = "border-rose-400 bg-rose-50 text-rose-600";
              else cls = "border-slate-200 bg-white text-slate-400 opacity-70";
            } else if (selected) cls = "border-[#0d9488] bg-[#0d9488]/5 text-[#0d9488] ring-1 ring-[#0d9488]/30";
            return (
              <button
                key={o.id}
                type="button"
                disabled={checked}
                onClick={() => setPick(i)}
                data-testid={`${beat.key}-instrument-${o.id}`}
                className={`border-2 rounded-md p-2 transition-all flex flex-col items-center ${cls}`}
              >
                {ICONS[o.icon] || ICONS.forceps}
                <span className="text-[11px] font-semibold leading-tight mt-1 text-center">{o.name}</span>
                {checked && (o.correct ? <Check className="w-4 h-4 mt-0.5" /> : selected ? <X className="w-4 h-4 mt-0.5" /> : null)}
              </button>
            );
          })}
        </div>
      </div>

      {checked && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid={`${beat.key}-note`}>
          <span className="font-semibold text-emerald-700">{correct.name}:</span> {correct.note}
        </div>
      )}
    </BeatShell>
  );
}
